import { ProductListGroupingType } from '@epicuro-next/data/engagement/engagement-models';
import { List } from '@epicuro-next/models/engagement';
import { createAction, props } from '@ngrx/store';
import { Product, SearchSortType } from 'graphql-api/gql-api-interfaces';

import { CommercialListControlAction } from './commercial-list.model';
import { ListEntity, ListProductsQuery } from './model';

export enum QUERY_UPDATE_STRATEGY {
  MERGE = 'merge-query-params',
  OVERWRITE = 'overwrite',
}

export enum SORT_TYPE_PRODUCTS_UPDATE_STRATEGY {
  ENTITY = 'update-entity-products',
  OPENED_LIST = 'update-opened-list-products',
}

export const setOperableListId = createAction(
  '[List entities] Set operable list id',
  props<{ listId: string | null }>(),
);

export const setUpdatingList = createAction(
  '[List entities] Updating list',
  props<{ listId: string; isUpdating: boolean }>(),
);

export const loadList = createAction(
  '[List entities] Load list',
  props<{ listId: string }>(),
);

export const upsertListEntity = createAction(
  '[List entities] Upsert list entity',
  props<{ listEntity: ListEntity }>(),
);

export const upsertManyListEntities = createAction(
  '[List entities] Upsert many list entities',
  props<{ listEntities: ListEntity[] }>(),
);

/**
 * Update the "list" property under the list entity
 */
export const updateEntityList = createAction(
  '[List entities] Update entity list',
  props<{ list: List }>(),
);

/**
 * Resests the last knwon query and clears product references
 */
export const resetListProducts = createAction(
  '[List entities] Reset list',
  props<{ listId: string }>(),
);

/**
 * Fetches products of the list by using provided or existing query.
 * If the query is changed (except start field), it clears product references
 *
 * @param props.listId
 * @param props.query is optional to overwrite/merge existing query
 * @param props.strategy defaults to merge
 */
export const loadNextListProducts = createAction(
  '[List entities] Load next list products',
  (args: {
    listId: string;
    query?: Omit<ListProductsQuery, 'productListId'>;
    strategy?: QUERY_UPDATE_STRATEGY;
  }) => ({ strategy: QUERY_UPDATE_STRATEGY.MERGE, ...args }),
);

export const loadListAllProducts = createAction(
  '[List entities] Load list all products',
  props<{ listId: string }>(),
);

export const setSearchSortTypeInListEntity = createAction(
  '[List entities] Set Search Sort Type in List Entity',
  props<{
    listId: string;
    sortType: SearchSortType;
    // used to separate effects to avoid duplicated products requests
    strategy: SORT_TYPE_PRODUCTS_UPDATE_STRATEGY;
  }>(),
);

export const changeListGroupingTypeInListEntitySuccess = createAction(
  '[List entities] Set List Grouping Type in List Entity Success',
  props<{
    listId: string;
    groupingType: ProductListGroupingType;
  }>(),
);

/**
 * Inserts new product reference ids to listEntity.productRefs
 */
export const pushLoadedProductRefsToList = createAction(
  '[List entities] Add loaded product references to list',
  props<{
    listId: string;
    productIds: string[];
    query?: ListProductsQuery;
    count: number;
  }>(),
);

/**
 * Inserts new product reference ids to listEntity.productRefs
 * Since it's optimistic update, it doesn't take care of query and count updates
 * It also doesn't filter out duplicates
 */
export const pushProductRefsToListAsOptimisticUI = createAction(
  '[List entities] Add loaded product references to list as optimistic ui update',
  props<{
    listId: string;
    productIds: string[];
    addToTop?: boolean;
  }>(),
);

export const pushNewListProductsToListEntity = createAction(
  '[Product entities] Push new list products to list entity',
  props<{
    listId: string;
    products: Product[];
  }>(),
);

/**
 * Update the list products and count when a
 * product is removed from the list
 */
export const removeProductFromListEntity = createAction(
  '[List entities] Remove product from list',
  props<{
    listId: string;
    productId: string;
  }>(),
);

export const applyCommercialListControl = createAction(
  '[List entities] Apply commercial list control',
  props<{ listId: string; controlAction: CommercialListControlAction }>(),
);

export const resetListEntities = createAction(
  '[List entities] Reset list entities',
);

export const changeProductOrderInListEntity = createAction(
  '[List entities] Change product order in list',
  props<{
    listId: string;
    productId: string;
    brandId: string;
    order: number;
  }>(),
);

export const changeProductOrderInListEntitySuccess = createAction(
  '[List entities] Change product order in list success',
  props<{
    listId: string;
    productId: string;
    brandId: string;
    order: number;
  }>(),
);
